import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtMXN, fmtDate, downloadBlob, todayStr } from "../lib/api";
import Topbar from "../components/Topbar";
import StatusBadge from "../components/StatusBadge";
import EmptyState from "../components/EmptyState";
import PizzaSpinner from "../components/PizzaSpinner";
import { Plus, Search, Download, ShoppingBag, Check, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useTranslation } from "react-i18next";

// Fix WH90 (2026-10-29): avance de cada OC en los 7 pasos del ciclo de compras.
// El backend manda `po.avance` (GET /purchase-orders); aquí solo se dibuja.
const AVANCE_PASOS = [
  { n: 1, rol: "branch" }, { n: 2, rol: "corporate" }, { n: 3, rol: "supplier" }, { n: 4, rol: "branch" },
  { n: 5, rol: "supplier" }, { n: 6, rol: "corporate" }, { n: 7, rol: "finance" },
];
const AVANCE_ROL_CLASE = {
  branch: "bg-amber-700", corporate: "bg-slate-900", supplier: "bg-blue-700", finance: "bg-emerald-700",
};
// Clave del filtro por paso: "1".."7", "completa" o "detenida". Las compras externas no entran.
const claveAvance = (a) => {
  if (!a) return null;
  if (a.estado === "completa") return "completa";
  if (a.estado === "detenida") return "detenida";
  if (a.estado === "en_curso") return String(a.paso);
  return null;
};

function RolAvance({ rol }) {
  const { t } = useTranslation();
  if (!rol) return null;
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-semibold text-white whitespace-nowrap ${AVANCE_ROL_CLASE[rol]}`}>
      {t(`po.progress.roles.${rol}`)}
    </span>
  );
}

function AvanceOC({ avance, qtyPending }) {
  const { t } = useTranslation();
  if (!avance) return <span className="text-xs text-slate-400">—</span>;
  if (avance.estado === "externa") {
    return (
      <div data-testid="po-progress-external">
        <div className="text-xs font-bold text-slate-700">{t("po.progress.external")}</div>
        <div className="text-[11px] text-slate-500">{t("po.progress.externalHint")}</div>
      </div>
    );
  }
  const segmento = (n) => {
    if (avance.estado === "completa" || n < avance.paso) {
      // Factura antes de la recepción: el paso 5 ya pasó, el 4 todavía no.
      if (avance.aviso === "factura_sin_recepcion" && n === 4) return "bg-amber-500";
      return "bg-emerald-500";
    }
    if (avance.aviso === "factura_sin_recepcion" && n === 5) return "bg-emerald-500";
    if (n === avance.paso) {
      if (avance.estado === "detenida") return "bg-red-500";
      return avance.aviso ? "bg-amber-500" : "bg-[#FFDF00] ring-1 ring-inset ring-[#D4B800]";
    }
    return "bg-slate-200";
  };
  let titulo;
  let sub = null;
  let tono = "text-[#1A1A1A]";
  if (avance.estado === "completa") {
    titulo = <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> {t("po.progress.complete")}</span>;
    sub = t("po.progress.paid");
    tono = "text-emerald-700";
  } else if (avance.estado === "detenida") {
    titulo = t("po.progress.stoppedAt", { n: avance.paso });
    sub = avance.motivo === "cancelada" ? t("po.progress.cancelled") : t("po.progress.rejected");
    tono = "text-red-700";
  } else {
    titulo = `${t("po.progress.stepOf", { n: avance.paso })} · ${t(`po.progress.step${avance.paso}`)}`;
    if (avance.aviso === "factura_sin_recepcion") sub = t("po.progress.invoiceBeforeReceipt");
    else if (avance.aviso === "factura_rechazada") sub = t("po.progress.invoiceRejected");
    else if (avance.aviso === "precio_distinto") sub = t("po.progress.priceMismatch");
    else if (avance.paso === 2 && avance.firmas) sub = t("po.progress.signature", { n: avance.firma, total: avance.firmas });
    else if (avance.paso === 3) sub = qtyPending ? t("po.progress.qtyChangePending") : t("po.progress.waitingDelivery");
    else if (avance.paso === 5 && avance.fecha) sub = t("po.progress.receivedOn", { date: fmtDate(avance.fecha) });
    else if (avance.paso === 7) sub = avance.fecha ? t("po.progress.validatedDue", { date: fmtDate(avance.fecha) }) : t("po.progress.validated");
  }
  return (
    <div data-testid={`po-progress-${claveAvance(avance)}`}>
      <div className="flex gap-0.5 mb-1">
        {AVANCE_PASOS.map((p) => <span key={p.n} className={`block w-5 h-1.5 rounded-full ${segmento(p.n)}`} />)}
      </div>
      <div className={`text-xs font-bold whitespace-nowrap ${tono}`}>{titulo}</div>
      {(avance.rol || sub) && (
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mt-0.5 max-w-[240px]">
          {avance.estado === "en_curso" && <RolAvance rol={avance.rol} />}
          {sub && <span className={`text-[11px] leading-snug ${avance.aviso || avance.estado === "detenida" ? "text-amber-700" : "text-slate-500"}`}>{sub}</span>}
        </div>
      )}
    </div>
  );
}

export default function PurchaseOrders() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [list, setList] = useState(null);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [costCenters, setCostCenters] = useState([]);
  const [ccFilter, setCcFilter] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [pendingQty, setPendingQty] = useState({});
  const [paso, setPaso] = useState("all"); // Fix WH90: filtro por paso del avance
  const navigate = useNavigate();
  const verAvance = user?.role !== "supplier" && user?.role !== "direccion";

  const exportToExcel = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (status !== "all") params.set("status", status);
      if (ccFilter !== "all") params.set("cost_center", ccFilter);
      const res = await api.get(`/purchase-orders/export?${params.toString()}`, { responseType: "blob" });
      downloadBlob(res.data, `ordenes_compra_${todayStr()}.xlsx`);
      toast.success(t("export.success"));
    } catch {
      toast.error(t("export.error"));
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams();
    // Direccion solo ve compras externas
    if (user?.role === "direccion") params.set("po_type", "external_emergency");
    api.get(`/purchase-orders?${params.toString()}`).then((r) => setList(r.data)).catch(() => {});
    if (user?.role !== "direccion") {
      api.get("/cost-centers").then((r) => setCostCenters(r.data)).catch(() => setCostCenters([]));
    }
    api.get("/quantity-changes?status=pending")
      .then((r) => setPendingQty(Object.fromEntries((r.data || []).map((x) => [x.purchase_order_id, x]))))
      .catch(() => {});
  }, []);

  if (!list) return <><Topbar title={t("po.title")} /><PizzaSpinner /></>;

  // Fix WH90: centro de costo y búsqueda primero; los conteos por paso salen de aquí
  // (sin el filtro de estado ni el de paso).
  const base = list.filter((p) => {
    if (ccFilter !== "all" && p.cost_center_code !== ccFilter) return false;
    if (search && !`${p.folio} ${p.supplier_name} ${p.branch_name}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  const conteoPasos = {};
  base.forEach((p) => {
    const k = claveAvance(p.avance);
    if (k) conteoPasos[k] = (conteoPasos[k] || 0) + 1;
  });
  const filtered = base.filter((p) => {
    if (status === "qty_pending") {
      if (!pendingQty[p.id]) return false;
    } else if (status !== "all" && p.status !== status) return false;
    if (paso !== "all" && claveAvance(p.avance) !== paso) return false;
    return true;
  });
  const elegirPaso = (k) => {
    setPaso((prev) => (prev === k ? "all" : k));
    setStatus("all");
  };

  return (
    <>
      <Topbar title={t("po.title")} subtitle={t("po.subtitle", { count: list.length })} />
      <div className="p-6 space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          {(user?.role === "branch_manager" || user?.role === "corporate_admin") && (
            <button
              data-testid="po-new-btn"
              onClick={() => navigate("/ocs/nueva")}
              className="btn-primary-chizy px-4 py-2.5 rounded-xl text-sm flex items-center gap-2"
            >
              <Plus className="w-4 h-4" strokeWidth={2.5} /> {t("po.new")}
            </button>
          )}
          {(user?.role === "branch_manager" || user?.role === "corporate_admin") && (
            <button
              data-testid="po-external-new-btn"
              onClick={() => navigate("/ocs/externa-nueva")}
              className="px-4 py-2.5 rounded-xl text-sm flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-[#1A1A1A] font-bold transition"
            >
              <ShoppingBag className="w-4 h-4" strokeWidth={2.5} /> Cargar compra externa
            </button>
          )}
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="po-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("po.searchPlaceholder")}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]"
            />
          </div>
          {user?.role !== "direccion" && (
            <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl p-1">
              {[
                { v: "all", l: t("po.filterAll") },
                { v: "pending_approval", l: t("po.filterPending") },
                { v: "approved", l: t("po.filterApproved") },
                { v: "rejected", l: t("po.filterRejected") },
                { v: "qty_pending", l: t("poQty.filterPending") },
              ].map((f) => (
                <button
                  key={f.v}
                  data-testid={`po-filter-${f.v}`}
                  onClick={() => { setStatus(f.v); setPaso("all"); }}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                    status === f.v ? "bg-[#1A1A1A] text-white" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {f.l}
                </button>
              ))}
            </div>
          )}
          {user?.role !== "direccion" && (
            <select
              data-testid="po-cc-filter"
              value={ccFilter}
              onChange={(e) => setCcFilter(e.target.value)}
              className="px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]"
            >
              <option value="all">{t("po.allCostCenters")}</option>
              {costCenters.map((cc) => (
                <option key={cc.code} value={cc.code}>{cc.code} — {cc.name}</option>
              ))}
            </select>
          )}
          <button data-testid="po-export-btn" onClick={exportToExcel} disabled={exporting}
            className="ml-auto inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold disabled:opacity-50">
            <Download className="w-4 h-4" /> {exporting ? t("export.exporting") : t("export.button")}
          </button>
        </div>

        {verAvance && (
          <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="po-progress-strip">
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
              <h3 className="font-display font-bold text-sm text-[#1A1A1A]">{t("po.progress.title")}</h3>
              <span className="text-xs text-slate-500">{t("po.progress.hint")}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-2">
              {AVANCE_PASOS.map((p) => {
                const k = String(p.n);
                const activo = paso === k;
                const contenido = (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 shrink-0 rounded-full bg-[#FFDF00] text-[#1A1A1A] text-xs font-extrabold flex items-center justify-center">{p.n}</span>
                      <span className="text-xs font-bold leading-tight text-[#1A1A1A]">{t(`po.progress.step${p.n}`)}</span>
                    </div>
                    <div className="flex items-end justify-between gap-1 mt-2">
                      <RolAvance rol={p.rol} />
                      {p.n !== 1 && <span className="text-xl font-extrabold leading-none text-[#1A1A1A]">{conteoPasos[k] || 0}</span>}
                    </div>
                  </>
                );
                // El paso 1 no es filtro: toda OC ya lo cumplió al crearse.
                if (p.n === 1) {
                  return <div key={k} title={t("po.progress.step1Hint")} className="text-left rounded-xl border border-slate-200 p-2.5">{contenido}</div>;
                }
                return (
                  <button key={k} type="button" data-testid={`po-step-filter-${k}`} onClick={() => elegirPaso(k)}
                    className={`text-left rounded-xl border p-2.5 transition ${activo ? "border-[#1A1A1A] ring-1 ring-[#1A1A1A] bg-[#FFFBE6]" : "border-slate-200 hover:bg-slate-50"}`}>
                    {contenido}
                  </button>
                );
              })}
              {[
                { k: "completa", l: t("po.progress.done"), h: t("po.progress.doneHint"), icon: <Check className="w-3.5 h-3.5" />, c: "bg-emerald-50 border-emerald-200", n: "bg-emerald-500" },
                { k: "detenida", l: t("po.progress.stopped"), h: t("po.progress.stoppedHint"), icon: <AlertCircle className="w-3.5 h-3.5" />, c: "bg-red-50 border-red-200", n: "bg-red-500" },
              ].map((x) => (
                <button key={x.k} type="button" data-testid={`po-step-filter-${x.k}`} onClick={() => elegirPaso(x.k)}
                  className={`text-left rounded-xl border p-2.5 transition ${x.c} ${paso === x.k ? "ring-2 ring-[#1A1A1A]" : ""}`}>
                  <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 shrink-0 rounded-full text-white flex items-center justify-center ${x.n}`}>{x.icon}</span>
                    <span className="text-xs font-bold text-[#1A1A1A]">{x.l}</span>
                  </div>
                  <div className="flex items-end justify-between gap-1 mt-2">
                    <span className="text-[10px] text-slate-500 leading-tight">{x.h}</span>
                    <span className="text-xl font-extrabold leading-none text-[#1A1A1A]">{conteoPasos[x.k] || 0}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {filtered.length === 0 ? (
          <EmptyState
            title={t("dashboard.noOrdersTitle")}
            message={user?.role === "branch_manager" ? t("po.noPosEmptyBranch") : t("po.noPosEmptyOther")}
          />
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                  <tr>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.folio")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.branch")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.supplier")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.costCenterShort")}</th>
                    <th className="text-right px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.total")}</th>
                    {verAvance && <th className="text-left px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.progress.column")}</th>}
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.status")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.date")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((po, idx) => (
                    <motion.tr
                      key={po.id}
                      initial={{ opacity: 0, y: 3 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(idx * 0.02, 0.3) }}
                      onClick={() => navigate(`/ocs/${po.id}`)}
                      className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer"
                      data-testid={`po-row-${po.folio}`}
                    >
                      <td className="px-6 py-3.5 font-mono text-sm font-bold text-[#1A1A1A]">
                        <div className="flex items-center gap-1.5">
                          {po.folio}
                          {po.po_type === "external_emergency" && (
                            <span className="inline-block px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-bold rounded-md">EXT</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-3.5 text-sm">{po.branch_name}</td>
                      <td className="px-6 py-3.5 text-sm text-slate-700">
                        {po.po_type === "external_emergency"
                          ? (po.external_purchase?.supplier_name_free_text || "—")
                          : po.supplier_name}
                      </td>
                      <td className="px-6 py-3.5">
                        {po.cost_center_name ? (
                          <div className="flex flex-col">
                            <span className="text-sm text-slate-700">{po.cost_center_name}</span>
                            <span className="text-[10px] font-mono text-slate-400">{po.cost_center_code}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-3.5 text-sm font-bold text-right">{fmtMXN(po.total)}</td>
                      {verAvance && <td className="px-4 py-3.5"><AvanceOC avance={po.avance} qtyPending={!!pendingQty[po.id]} /></td>}
                      <td className="px-6 py-3.5"><StatusBadge status={po.status} /></td>
                      <td className="px-6 py-3.5 text-sm text-slate-500">{fmtDate(po.created_at)}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
